import React, { useEffect, useState, useMemo } from 'react';
import { arrayOf, bool, element, func, node, oneOf, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames';
import CreatableSelect from 'react-select/creatable';
import AsyncCreatableSelect from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import Select, { components } from 'react-select';
import { BASE_CLASS, CONTAINER_CLASS, MENU_PLACEMENTS, ERROR_CLASS } from './constants';
import SelectOption from './SelectOption';
import DefaultNoOptionsMessage from './NoOptionMessage';
import SelectTag from './SelectTag';
import SelectChevron from './SelectChevron';
import SelectClearIndicator from './SelectClearIndicator';
import SelectGroupLabel from './SelectGroupLabel';
import SelectControl from './SelectControl';
import { MenuComponent as MenuComponentBase } from './MenuComponent';

import './index.scss';

const createAndAppendPortal = (
    isInPortal,
    defaultPortalTarget,
    setDefaultPortalTarget
) => {
    if (defaultPortalTarget) {
        document.body.appendChild(defaultPortalTarget);
    } else {
        const div = document.createElement('div');
        div.classList.add(CONTAINER_CLASS);
        setDefaultPortalTarget(div);
    }

    return () => (
        (isInPortal && defaultPortalTarget)
            ? document.body.removeChild(defaultPortalTarget)
            : null
    );
};

const defaultStyles = {

    // This is required since the portaled menu is rendered outside of the .orca-combo-box-container, it can't be scoped
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    })
};

/**
 * @deprecated Please use Combobox from @fiverr-private/forms instead.
 */
const ComboBox = ({
    icon,
    className,
    classNamePrefix,
    name,
    options = [],
    placeholder,
    LoadingMessageComponent = components.LoadingMessage,
    isDisabled = false,
    isMulti = true,
    isClearable = false,
    isCreatable = false,
    isAsync = false,
    isLoading = false,
    isSearchable = true,
    controlShouldRenderValue = true,
    onChange = Function.prototype,
    onBlur = Function.prototype,
    onFocus = Function.prototype,
    onInputChange = Function.prototype,
    onMenuOpen = Function.prototype,
    menuPlacement = MENU_PLACEMENTS.AUTO,
    menuShouldBlockScroll = false,
    openMenuOnClick = true,
    menuIsOpen,
    isInPortal = true,
    value,
    defaultValue,
    inputValue,
    hasError = false,
    backspaceRemovesValue = false,
    id,
    formatGroupLabel = SelectGroupLabel,
    loadOptions,
    defaultOptions,
    hideSelectedOptions = true,
    ChevronComponent = SelectChevron,
    SingleValueComponent = components.SingleValue,
    OptionComponent = SelectOption,
    TagComponent = SelectTag,
    MenuComponent = MenuComponentBase,
    ClearIndicatorComponent = SelectClearIndicator,
    InputComponent = components.Input,
    noOptionsHide,
    NoOptionsMessage = DefaultNoOptionsMessage,
    filterOption = undefined,
    formatCreateLabel
}) => {
    const [defaultPortalTarget, setDefaultPortalTarget] = useState(null);

    const SelectComponent = useMemo(() => {
        if (isAsync && isCreatable) {return AsyncCreatableSelect;}

        if (isAsync) {return AsyncSelect;}

        if (isCreatable) {return CreatableSelect;}

        return Select;
    }, [isAsync, isCreatable]);

    const components = {
        IndicatorSeparator: null,
        DropdownIndicator: ChevronComponent,
        Option: OptionComponent,
        MultiValue: TagComponent,
        ClearIndicator: ClearIndicatorComponent,
        Control: SelectControl,
        Menu: MenuComponent,
        SingleValue: SingleValueComponent,
        LoadingMessage: LoadingMessageComponent,
        Input: InputComponent,
        NoOptionsMessage
    };

    useEffect(() => {
        if (!isInPortal) {return;}

        return createAndAppendPortal(
            isInPortal,
            defaultPortalTarget,
            setDefaultPortalTarget
        );
    }, [defaultPortalTarget, isInPortal]);

    const wrapperClasses = classNames(
        CONTAINER_CLASS,
        className,
        { [ERROR_CLASS]: hasError }
    );

    const prefixClasses = classNames(
        BASE_CLASS,
        classNamePrefix
    );

    return (
        <SelectComponent styles={defaultStyles}
            className={wrapperClasses}
            classNamePrefix={prefixClasses}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onInputChange={onInputChange}
            options={options}
            isLoading={isLoading}
            isMulti={isMulti}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            inputValue={inputValue}
            name={name}
            menuPlacement={menuPlacement}
            openMenuOnClick={openMenuOnClick}
            inputId={id}
            isClearable={isClearable}
            isDisabled={isDisabled}
            menuPortalTarget={isInPortal && defaultPortalTarget}
            menuShouldBlockScroll={menuShouldBlockScroll}
            backspaceRemovesValue={backspaceRemovesValue}
            components={components}
            isSearchable={isSearchable}
            formatGroupLabel={formatGroupLabel}
            formatCreateLabel={formatCreateLabel}
            loadOptions={loadOptions}
            defaultOptions={defaultOptions}
            onMenuOpen={onMenuOpen}
            hideSelectedOptions={hideSelectedOptions}
            menuIsOpen={menuIsOpen}
            filterOption={filterOption}
            controlShouldRenderValue={controlShouldRenderValue}
            noOptionsMessage={noOptionsHide === true ? () => null : undefined}
            icon={icon}/>
    );
};

ComboBox.displayName = 'ComboBox';

ComboBox.propTypes = {

    /**
     * A className to be outermost container of React-Select
     */
    className: string,

    /**
     * If provided, all inner components will be given a prefixed className attribute
     */
    classNamePrefix: string,

    /**
     * An identifier to be set as the id of the underlying input (used to associate a label)
     */
    id: string,

    /**
     * The input's name property.
     */
    name: string,

    /**
     * Value for controlled component
     */
    value: oneOfType([
        shape({
            value: node,
            label: node
        }),
        arrayOf(
            shape({
                value: node,
                label: node
            })
        )
    ]),

    /**
     * Value for uncontrolled component
     */
    defaultValue: oneOfType([
        shape({
            value: node,
            label: node
        }),
        arrayOf(
            shape({
                value: node,
                label: node
            })
        )
    ]),

    /**
     * Input's search value
     */
    inputValue: string,

    /**
     * Input's placeholder text
     */
    placeholder: string,

    /**
     * Whether to remove the selected value when backspace is pressed
     *
     * **Default**: false
     */
    backspaceRemovesValue: bool,

    /**
     * The position where options dropdown should be rendered: bottom, top or auto
     * (bottom, if enough height underneath the input, top otherwise)
     *
     * **Default**: 'auto'
     */
    menuPlacement: oneOf(Object.values(MENU_PLACEMENTS)),

    /**
     * Whether menu should block scroll when open
     *
     * **Default**: false
     */
    menuShouldBlockScroll: bool,

    /**
     * Whether input should be disabled
     *
     * **Default**: false
     */
    isDisabled: bool,

    /**
     * Whether input should allow multiple values to be selected
     * Or should be a singular Select
     *
     * **Default**: true
     */
    isMulti: bool,

    /**
     * Whether 'clear all selected values' button should be present
     *
     * **Default**: false
     */
    isClearable: bool,

    /**
     * Whether to allow creation of new values new values

     * **Default**: false
     */
    isCreatable: bool,

    /**
     * Whether to allow getting options asynchronically
     *
     * **Default**: false
     */
    isAsync: bool,

    /**
     * Whether to show loading indicator
     */
    isLoading: bool,

    /**
     * Whether to allow searching the options
     *
     * **Default**: true
     */
    isSearchable: bool,

    /**
     * Whether the value of the select, e.g. SingleValue, should be displayed in the control
     *
     * **Default**: true
     */
    controlShouldRenderValue: bool,

    /**
     * whether to show already selected options in the Suggestions dropdown
     */
    hideSelectedOptions: bool,

    /**
     * onChange (value selected) event handler
     */
    onChange: func,

    /**
     * Whether open Suggestions menu when user clicks on input
     */
    openMenuOnClick: bool,

    /**
     * onBlur event handler
     */
    onBlur: func,

    /**
     * onFocus event handler
     */
    onFocus: func,

    /**
     * onInputChange (user typed) event handler
     */
    onInputChange: func,

    /**
     * Allows control of whether the menu is opened when the Select is focused
     */
    onMenuOpen: func,

    /**
     * Control if menu is open programmatically
     */
    menuIsOpen: bool,

    /**
     * Array of possible options (suggestions)
     */
    options: arrayOf(shape({
        value: string,
        label: string
    })),

    /**
     * Whether Suggestions dropdown should be rendered
     * Inside a portal
     * (to fix an issue with positioning when used with Fiverr modal)
     *
     * **Default**: True
     */
    isInPortal: bool,

    /**
     * Show error state
     */
    hasError: bool,

    /**
     * Override the default create label, can be used when isCreatable=true.
     */
    formatCreateLabel: func,

    /**
      * Override the default group label
      */
    formatGroupLabel: func,

    /**
      * The default set of options to show before the user starts searching. When set to true, the results for loadOptions('') will be autoloaded.
      */
    defaultOptions: oneOfType([
        arrayOf(shape({
            value: node,
            label: node
        })),
        bool
    ]),

    /**
     * Don't show 'no options' block when there are no options (usually used when `isCreatable` is enabled)
     */
    noOptionsHide: bool,

    /**
      * Function that returns a promise, which is the set of options to be used once the promise resolves.
      */
    loadOptions: func,

    /**
      * Either an `<svg>` element or an `<Icon>` component.
      */
    icon: element,

    /**
     * Component to be used when value is selected in single-value-selection
     * (isMulti === false) mode
     */
    SingleValueComponent: oneOfType([func, element]),

    /**
     * Wrapper component for the dropdown menu
     */
    MenuComponent: oneOfType([func, element]),

    /**
     * Override for Tag (selected value when isMulti is true) component
     * More:  https://react-select.com/components
     */
    TagComponent: oneOfType([func, element]),

    /**
     * Override for Suggestion Option component
     * More:  https://react-select.com/components
     */
    OptionComponent: oneOfType([func, element]),

    /**
     * Override for DropdownIndicator component
     * More:  https://react-select.com/components
     */
    ChevronComponent: oneOfType([func, element]),

    /**
     * Override for ClearIndicator component
     * More:  https://react-select.com/components
     */
    ClearIndicatorComponent: oneOfType([func, element]),

    /**
     * Override for Input component
     * More:  https://react-select.com/components
     */
    InputComponent: oneOfType([func, element]),

    /**
     * Loading message for when isLoading === true
     * More:  https://react-select.com/components
     */
    LoadingMessageComponent: string,

    /**
     * Message to be displayed in the menu if there are no options passed in.
     * Use `noOptionsHide` if you need to hide this component completely
     * More:  https://react-select.com/components
     */
    NoOptionsMessage: oneOfType([func, element]),

    /**
     * Custom method to filter whether an option should be displayed in the menu
     * More:  https://react-select.com/props
     */
    filterOption: func
};

Object.assign(ComboBox, {
    MENU_PLACEMENTS
});

ComboBox.components = {
    Chevron: SelectChevron,
    SingleValue: components.SingleValue,
    Option: SelectOption,
    Tag: SelectTag,
    Menu: MenuComponentBase,
    ClearIndicator: SelectClearIndicator,
    Input: components.Input,
    NoOptionsMessage: components.NoOptionsMessage,
    LoadingMessage: components.LoadingMessage
};

export default ComboBox;
